<template>
  <div class="dashboard-page technician-dashboard">
    <div class="dashboard-sticky-area">
      <div class="dashboard-topbar dashboard-heading">
        <h4 class="dashboard-page-title dashboard-title">
          <CIcon name="cil-speedometer" class="dashboard-title-icon"></CIcon>
          {{ $t("nav.dashboard") }}
        </h4>

        <div class="dashboard-header-right">
          <!-- Global Dashboard Search -->
          <div class="dashboard-search-box" ref="searchBoxRef">
            <CIcon name="cil-search" class="search-icon" />
            <input
              type="text"
              v-model="searchQuery"
              :placeholder="dashboardText('ค้นหา...', 'Search...')"
              class="dashboard-search-input"
              @keydown="onKeyboardSearch" v-listbox-escape="closeKeyboardSearch"
              role="combobox" aria-autocomplete="list" :aria-controls="keyboardSearchId"
              :aria-expanded="String(Boolean(searchQuery && showSearchDropdown))"
              :aria-activedescendant="searchQuery && showSearchDropdown && keyboardSearchIndex >= 0 ? keyboardSearchId + '-' + keyboardSearchIndex : null"
              :aria-label="$t('common.search')"
              @focus="showSearchDropdown = true"
            />
            <button
              v-if="searchQuery"
              type="button"
              class="search-clear-btn" :aria-label="$t('common.clearSearch')"
              @click="clearSearch"
            >
              <CIcon name="cil-x" />
            </button>

            <!-- Search Results Dropdown List -->
            <div
              v-if="searchQuery && showSearchDropdown"
              class="search-dropdown-menu" :id="keyboardSearchId" role="listbox" :aria-label="$t('common.search')"
            >
              <div class="search-dropdown-header">
                <span>{{ dashboardText('ผลการค้นหา (' + searchMatches.length + ' รายการ)', 'Search Results (' + searchMatches.length + ')') }}</span>
                <small v-if="searchMatches.length">{{ dashboardText('คลิกเพื่อไปยังรายการ', 'Click to jump to item') }}</small>
              </div>

              <div v-if="!searchMatches.length" class="p-3 text-center text-muted small">
                {{ dashboardText('ไม่พบข้อมูลที่ตรงกัน (ค้นหาได้ทั้งรหัสอาคาร, ชื่อไทย-อังกฤษ, ห้อง, หรือเลขที่แจ้ง)', 'No matches found (search by building, TH/EN name, room, or ticket #)') }}
              </div>

              <div
                v-for="(m, idx) in searchMatches"
                :key="m.id + '-' + idx"
                class="search-dropdown-item keyboard-listbox-option" :id="keyboardSearchId + '-' + idx" role="option" :aria-selected="String(keyboardSearchIndex === idx)" :data-keyboard-active="keyboardSearchIndex === idx" @mousedown.prevent
                @click="onSelectSearchResult(m)"
              >
                <span class="item-badge" :class="m.badgeClass">{{ m.sectionLabel }}</span>
                <div class="item-content">
                  <div class="item-title">{{ m.title }}</div>
                  <div class="item-desc">{{ m.description }}</div>
                  <small v-if="m.meta" class="text-muted d-block mt-1">{{ m.meta }}</small>
                </div>
              </div>
            </div>
          </div>

          <!-- Date Range Filter Hotkeys (Right-aligned) -->
          <div class="dashboard-filter">
            <div v-if="selectedRange === 'custom'" class="custom-date-range">
              <input type="date" v-model="startDate" @change="onCustomDateChange" />
              <span>-</span>
              <input type="date" v-model="endDate" @change="onCustomDateChange" />
            </div>

            <div class="range-tabs">
              <button
                v-for="range in rangeOptions"
                :key="range.value"
                type="button"
                class="range-tab"
                :class="{
                  active: selectedRange === range.value,
                  'filter-tab': range.value === 'custom'
                }"
                @click="selectRange(range.value)"
              >
                <CIcon v-if="range.value === 'custom'" name="cil-filter" />
                <span v-else>{{ range.label }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="kpi-grid">
      <div class="kpi-card kpi-clickable" @click="$router.push('/technician/maintechnician')" v-keyboard-activate role="button" tabindex="0">
        <div class="kpi-card-head"><span>{{ $t("dashboard.kpiTotalRequests") }}</span>
          <div class="kpi-icon icon-blue">
            <CIcon name="cil-bell" />
          </div>
        </div>
        <div class="kpi-value">{{ allAssignedCount }}</div>
        <div class="kpi-footer"><span class="kpi-badge kpi-badge-blue">{{ dashboardText('ทั้งหมด', 'TOTAL') }}</span></div>
      </div>

      <div class="kpi-card kpi-clickable" @click="$router.push({ path: '/technician/maintechnician', query: { status: 'active' } })" v-keyboard-activate role="button" tabindex="0">
        <div class="kpi-card-head"><span>{{ $t("dashboard.kpiInProgress") }}</span>
          <div class="kpi-icon icon-orange">
            <CIcon name="cil-av-timer" />
          </div>
        </div>
        <div class="kpi-value">{{ stats.inProgress }}</div>
        <div class="kpi-footer"><span class="kpi-badge kpi-badge-active">{{ dashboardText('กำลังทำ', 'ACTIVE') }}</span></div>
      </div>

      <div class="kpi-card kpi-clickable" @click="$router.push({ path: '/technician/maintechnician', query: { status: 'overdue' } })" v-keyboard-activate role="button" tabindex="0">
        <div class="kpi-card-head"><span>{{ $t("dashboard.kpiOverSla") }}</span>
          <div class="kpi-icon icon-red">
            <CIcon name="cil-clock" />
          </div>
        </div>
        <div class="kpi-value">{{ stats.overdue }}</div>
        <div class="kpi-footer"><span class="kpi-badge kpi-badge-red">SLA</span></div>
      </div>

      <div class="kpi-card kpi-clickable" @click="$router.push('/technician/history')" v-keyboard-activate role="button" tabindex="0">
        <div class="kpi-card-head"><span>{{ $t("dashboard.kpiCompleted") }}</span>
          <div class="kpi-icon icon-green">
            <CIcon name="cil-check-circle" />
          </div>
        </div>
        <div class="kpi-value">{{ stats.completed }}</div>
        <div class="kpi-footer">
          <span class="kpi-badge kpi-badge-green">{{ dashboardText('ปิดงานแล้ว', 'DONE') }}</span>
          <span v-if="stats.satisfaction" class="kpi-rating-badge">
            <CIcon name="cil-star" class="text-warning mr-1" />{{ stats.satisfaction }}/5
          </span>
        </div>
      </div>
    </div>

    <!-- 1. Personal Analytics & Work Progress (ELEVATED BEFORE TICKETS) -->
    <div class="dashboard-main-grid mb-3">
      <div class="dashboard-left">
        <div class="panel-card highlight-card">
          <div class="panel-header">
            <div>
              <h4>{{ $t("dashboard.projectAnalytics") }}</h4>
              <p>{{ barChartSubtitle }}</p>
            </div>
            <button type="button" class="panel-more" :title="dashboardText('ตัวเลือก', 'Options')">
              <CIcon name="cil-options" />
            </button>
          </div>
          <div class="chart-divider"></div>
          <div class="chart-wrapper">
            <div v-if="barLabels.length" class="dashboard-trend-container">
              <div class="dashboard-trend-legend">
                <span class="trend-legend-item">
                  <i class="trend-legend-dot blue"></i>
                  {{ $t("ticketDetail.tech.receivedJobsCount") }}
                </span>
              </div>
              <div class="dashboard-trend-scroll">
                <div class="dashboard-trend-chart">
                  <div v-for="(item, idx) in barTrendItems" :key="idx" class="trend-col">
                    <div class="trend-bars-group single-bar">
                      <div class="trend-bar-wrapper" :title="`${item.label}: ${item.count}`">
                        <span class="trend-val">{{ item.count }}</span>
                        <div class="trend-bar blue" :style="{ height: item.height + 'px' }"></div>
                      </div>
                    </div>
                    <span class="trend-label">{{ item.label }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="empty-chart">
              <CIcon name="cil-chart-line" size="3xl" /><span>{{ $t("dashboard.noData") }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="dashboard-right">
        <div class="panel-card progress-card">
          <div class="panel-header compact-header">
            <div>
              <h4>{{ $t("dashboard.workProgress") }}</h4>
              <p>{{ $t("dashboard.workProgressSubtitle") }}</p>
            </div>
          </div>
          <div class="progress-body">
            <div class="donut-wrapper">
              <svg class="donut-svg" viewBox="0 0 200 200">
                <circle
                  class="donut-background"
                  cx="100"
                  cy="100"
                  r="72"
                  pathLength="100"
                />
                <circle
                  class="donut-progress"
                  cx="100"
                  cy="100"
                  r="72"
                  pathLength="100"
                  :style="{ strokeDasharray: `${workProgressPercent} 100` }"
                />
              </svg>
              <div class="donut-center">
                <strong class="donut-percentage">{{ workProgressPercent }}%</strong>
                <span class="donut-caption">{{ dashboardText('ความสำเร็จ', 'Completed') }}</span>
              </div>
            </div>

            <div class="progress-stats-grid">
              <div class="progress-stat-pill">
                <div class="stat-pill-label">
                  <span class="stat-dot dot-green"></span>
                  <span>{{ $t("dashboard.kpiCompleted") }}</span>
                </div>
                <strong class="stat-pill-val text-success">{{ stats.completed }}</strong>
              </div>

              <div class="progress-stat-pill">
                <div class="stat-pill-label">
                  <span class="stat-dot dot-purple"></span>
                  <span>{{ $t("dashboard.kpiInProgress") }}</span>
                </div>
                <strong class="stat-pill-val text-primary">{{ stats.inProgress }}</strong>
              </div>

              <div class="progress-stat-pill">
                <div class="stat-pill-label">
                  <span class="stat-dot dot-blue"></span>
                  <span>{{ $t("dashboard.kpiTotalRequests") }}</span>
                </div>
                <strong class="stat-pill-val text-dark">{{ stats.total }}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 2. Immediate Action Workplace: Technician Ticket List (Hidden on mobile) -->
    <div ref="ticketPanel" class="panel-card ticket-panel mb-3 d-none d-md-block">
      <div class="panel-header ticket-header d-flex justify-content-between align-items-center flex-wrap">
        <div class="d-flex align-items-center">
          <CIcon name="cil-list" class="mr-2 text-primary" />
          <h4 class="mb-0 d-inline-block">{{ $t("dashboard.ticketDetails") }}</h4>
          <span class="badge badge-primary ml-2 py-1 px-2 font-weight-bold">{{ formattedTickets.length }} {{ dashboardText('รายการ', 'items') }}</span>
        </div>
      </div>
      <div class="ticket-table-wrapper d-none d-md-block">
        <CDataTable
          :items="formattedTickets"
          :fields="reportFields"
          :items-per-page-select="itemsPerPageSelectConfig"
          :items-per-page="tableItemsPerPage"
          :active-page.sync="tableCurrentPage"
          @pagination-change="tableItemsPerPage = $event"
          sorter
          hover
          pagination
        >
          <template #ticketNo="{ item }">
            <td :id="'ticket-row-' + item.ticketNo" class="font-weight-bold" :data-label="$t('dashboard.field.ticketNo')">
              <a href="javascript:void(0)" class="dashboard-ticket-link" @click="goToTicketDetail(item)" :title="dashboardText('เปิดดูในหน้า Home', 'View in Home')">
                {{ item.ticketNo }}
                <CIcon name="cil-external-link" class="ticket-link-icon" />
              </a>
            </td>
          </template>
          <template #dateInformed="{ item }">
            <td :data-label="$t('dashboard.field.dateInformed')">{{ item.dateInformed }}</td>
          </template>
          <template #reporter="{ item }">
            <td :data-label="$t('dashboard.field.reporterName')">{{ item.reporter }}</td>
          </template>
          <template #category="{ item }">
            <td :data-label="$t('dashboard.field.category')">{{ item.category }}</td>
          </template>
          <template #sla="{ item }">
            <td :data-label="$t('dashboard.field.sla')">
              <CBadge :color="item._slaOverdue ? 'danger' : 'success'">{{ item.sla }}</CBadge>
              <small v-if="item._sla_hours" class="d-block text-muted">
                {{ $t("dashboard.slaTarget") }} {{ item._sla_hours }}h
              </small>
            </td>
          </template>
          <template #status="{ item }">
            <td :data-label="$t('dashboard.field.status')">
              <CBadge :color="getBadge(item._status_raw)">{{ item.status }}</CBadge>
            </td>
          </template>
          <template #assignee="{ item }">
            <td :data-label="$t('dashboard.field.assignee')">{{ item.assignee }}</td>
          </template>
          <template #closedDate="{ item }">
            <td :data-label="$t('dashboard.field.closedDate')">{{ item.closedDate }}</td>
          </template>
        </CDataTable>
      </div>
    </div>

      <!-- Mobile Ticket Cards (media max-width: 768px) -->
      <div class="mobile-ticket-card-list d-md-none">
        <div
          v-for="(item, idx) in formattedTickets"
          :key="'m-tech-rep-' + item.ticketNo + '-' + idx"
          :id="'m-tech-rep-' + item.ticketNo + '-' + idx"
          class="mobile-ticket-card"
          @click="goToTicketDetail(item)"
        >
          <div class="mobile-ticket-card-header">
            <span class="mobile-ticket-id">{{ item.ticketNo }}</span>
            <span class="mobile-ticket-badge" :class="'badge-' + getBadge(item._status_raw)">
              {{ item.status }}
            </span>
          </div>
          <div class="mobile-ticket-card-body">
            <div class="mobile-card-row">
              <span class="mobile-row-label">{{ $t("dashboard.field.reporterName") }}</span>
              <span class="mobile-row-val">{{ item.reporter }}</span>
            </div>
            <div class="mobile-card-row">
              <span class="mobile-row-label">{{ $t("dashboard.field.category") }}</span>
              <span class="mobile-row-val">{{ item.category }}</span>
            </div>
            <div class="mobile-card-row">
              <span class="mobile-row-label">{{ $t("dashboard.field.dateInformed") }}</span>
              <span class="mobile-row-val">{{ item.dateInformed }}</span>
            </div>
            <div class="mobile-card-row">
              <span class="mobile-row-label">{{ $t("dashboard.field.assignee") }}</span>
              <span class="mobile-row-val">{{ item.assignee }}</span>
            </div>
            <div class="mobile-card-row">
              <span class="mobile-row-label">{{ $t("dashboard.field.sla") }}</span>
              <span class="mobile-row-val">
                <CBadge :color="item._slaOverdue ? 'danger' : 'success'">{{ item.sla }}</CBadge>
                <small v-if="item._sla_hours" class="ml-1 text-muted">
                  ({{ $t("dashboard.slaTarget") }} {{ item._sla_hours }}h)
                </small>
              </span>
            </div>
            <div v-if="item.closedDate && item.closedDate !== '-'" class="mobile-card-row">
              <span class="mobile-row-label">{{ $t("dashboard.field.closedDate") }}</span>
              <span class="mobile-row-val">{{ item.closedDate }}</span>
            </div>
          </div>
          <button type="button" class="mobile-view-details-btn" @click.stop="goToTicketDetail(item)">
            {{ dashboardText('ดูรายละเอียด', 'View Details') }}
          </button>
        </div>
        <div v-if="!formattedTickets.length" class="text-center py-4 text-muted">
          {{ $t("dashboard.noData") }}
        </div>
      </div>
    <TicketDetailModal v-if="selectedReport" @close="selectedReport=null">
      <ReportDetail :report="selectedReport" @close="selectedReport=null" @refresh="fetchData" />
    </TicketDetailModal>
  </div>
</template>

<script>
import keyboardActivate from '@/directives/keyboardActivate';
import listboxEscape from '@/directives/listboxEscape';
import keyboardSearch from '@/mixins/keyboardSearch';
import Service from "@/service/api";
import moment from "moment";
import Chart from "chart.js";
import { CChartBar } from "@coreui/vue-chartjs";
import TicketDetailModal from "@/components/common/TicketDetailModal.vue";
import ReportDetail from "./ReportDetail.vue";
import { matchTicketBilingual } from "@/utils/buildingBilingual.js";
import { getTicketLocationFull, getTicketReporterAffiliation, getTechnicianEffectiveStatus } from "@/utils/ticketViewHelper.js";

/*
 * Rounded Bar สำหรับ Chart.js 2.x
 * ทำหัวแท่งมนชัดเจน โดยไม่เปลี่ยนข้อมูลของกราฟ
 */
const originalRectangleDraw =
  Chart.elements.Rectangle.prototype.draw;

Chart.elements.Rectangle.prototype.draw = function () {
  const ctx = this._chart.ctx;
  const vm = this._view;

  /*
   * ใช้ custom rounded เฉพาะ BAR แนวตั้ง
   * element อื่น fallback กลับไปใช้ Chart.js เดิม
   */
  if (
    typeof vm.x !== "number" ||
    typeof vm.y !== "number" ||
    typeof vm.base !== "number" ||
    typeof vm.width !== "number"
  ) {
    return originalRectangleDraw.apply(this, arguments);
  }

  const left = vm.x - vm.width / 2;
  const right = vm.x + vm.width / 2;

  const top = Math.min(
    vm.y,
    vm.base
  );

  const bottom = Math.max(
    vm.y,
    vm.base
  );

  const barHeight =
    bottom - top;

  /*
   * ทำหัวมนชัด
   * สูงสุดครึ่งหนึ่งของความกว้างแท่ง
   */
  const radius = Math.min(
    vm.width / 2,
    10,
    barHeight / 2
  );

  ctx.save();

  ctx.beginPath();

  ctx.fillStyle =
    vm.backgroundColor;

  ctx.strokeStyle =
    vm.borderColor;

  ctx.lineWidth =
    vm.borderWidth || 0;

  /*
   * เริ่มจากล่างซ้าย
   */
  ctx.moveTo(
    left,
    bottom
  );

  /*
   * ขึ้นไปก่อนถึงมุมซ้ายบน
   */
  ctx.lineTo(
    left,
    top + radius
  );

  /*
   * มุมซ้ายบนมน
   */
  ctx.quadraticCurveTo(
    left,
    top,
    left + radius,
    top
  );

  /*
   * เส้นด้านบน
   */
  ctx.lineTo(
    right - radius,
    top
  );

  /*
   * มุมขวาบนมน
   */
  ctx.quadraticCurveTo(
    right,
    top,
    right,
    top + radius
  );

  /*
   * ลงล่างขวา
   */
  ctx.lineTo(
    right,
    bottom
  );

  /*
   * ปิดด้านล่างเป็นเหลี่ยม
   */
  ctx.lineTo(
    left,
    bottom
  );

  ctx.closePath();
  ctx.fill();

  if (vm.borderWidth) {
    ctx.stroke();
  }

  ctx.restore();
};
export default {
  directives: { keyboardActivate, listboxEscape },
  mixins: [keyboardSearch],
  name: "TechnicianDashboard",
  components: {
    CChartBar,
    TicketDetailModal,
    ReportDetail,
  },

  data() {
    return {
      selectedReport: null,
      selectedRange: "week",
      startDate: moment().startOf("isoWeek").format("YYYY-MM-DD"),
      endDate: moment().endOf("isoWeek").format("YYYY-MM-DD"),
      searchQuery: "",
      currentMatchIndex: 0,
      showSearchDropdown: false,
      hasNavigatedMatch: false,
      tickets: [],
      categories: [],
      stats: {
        total: 0,
        inProgress: 0,
        completed: 0,
        overdue: 0,
        satisfaction: 0,
      },
      tableItemsPerPage: 5,
      tableCurrentPage: 1,
      currentRepairWorkload: {
        personId: null,
        name: "",
        surname: "",
        role: "technician",
        currentWorkload: 0,
        workloadPercent: 0,
        activeTicketCount: 0,
        currentTickets: [],
      },
      workloadLoading: false,
      workloadError: null,
    };
  },

  computed: {
    tableFilterConfig() {
      return {
        label: this.$t('table.filterLabel'),
        placeholder: this.$t('table.filterPlaceholder')
      };
    },

    itemsPerPageSelectConfig() {
      return {
        label: this.$t('table.itemsPerPageLabel'),
        values: [10, 20, 50]
      };
    },

    currentLang() {
      return this.$store?.state?.locale?.lang || "th";
    },

    workProgressPercent() {
      if (!this.stats.total) return 0;
      return Math.round((this.stats.completed / this.stats.total) * 100);
    },

    currentTechnicianId() {
      try {
        const profile = this.$store?.getters?.["auth/profile"];
        if (profile && profile._id) return String(profile._id);
        const user = JSON.parse(localStorage.getItem("user"));
        return String(user?._id || user?.id || "");
      } catch {
        return "";
      }
    },

    allAssignedCount() {
      return this.dateFilteredTickets.length;
    },

    rangeOptions() {
      return [
        { label: this.$t("dashboard.rangeWeek"), value: "week" },
        { label: this.$t("dashboard.rangeMonth"), value: "month" },
        { label: this.$t("dashboard.rangeYear"), value: "year" },
        { value: "custom" },
      ];
    },

    dateFilteredTickets() {
      const now = moment();
      const techId = this.currentTechnicianId;
      return this.tickets.filter((ticket) => {
        if (ticket.status === "cancelled") return false;
        const assignments = ticket.supervisorPart?.assignments || [];
        if (assignments.length > 0 && techId) {
          const myAssignment = assignments.find(
            (item) => String(item.assignee?._id || item.assignee || "") === String(techId)
          );
          if (myAssignment && myAssignment.status === "rejected") return false;
        }
        const date = this.getTicketDate(ticket);

        switch (this.selectedRange) {
          case "week": return date.isSame(now, "isoWeek");
          case "month": return date.year() === now.year();
          case "year": {
            const startYear = now.year() - 4;
            return date.year() >= startYear && date.year() <= now.year();
          }
          case "custom": {
            if (!this.startDate || !this.endDate) return true;
            return date.isBetween(moment(this.startDate).startOf("day"), moment(this.endDate).endOf("day"), undefined, "[]");
          }
          case "all":
          default: return true;
        }
      });
    },

    searchMatches() {
      if (!this.searchQuery || !this.searchQuery.trim()) return [];
      const q = this.searchQuery.trim();
      const allTickets = Array.isArray(this.tickets) ? this.tickets : [];
      const matches = allTickets.filter((t) => matchTicketBilingual(t, q));

      return matches.slice(0, 30).map((t, idx) => {
        const ticketNo = t.ticketNumber || (t._id || '').substring(0, 8).toUpperCase() || '-';
        const loc = getTicketLocationFull(t, this.$t, this.$store.state.locale.lang) || '-';
        const rawStatus = (t.status || '').toLowerCase();
        const prob = t.userPart?.problemDetails || '-';
        const reporter = this.getReporterName ? this.getReporterName(t) : (t.userPart?.fullname || t.userPart?.username || '-');
        const badgeColor = this.getBadge ? this.getBadge(rawStatus) : 'ticket';

        return {
          id: t._id || t.ticketNumber || idx,
          ticket: t,
          title: `${ticketNo} · ${loc}`,
          description: prob,
          meta: reporter ? `${this.dashboardText('ผู้แจ้ง', 'Reporter')}: ${reporter}` : '',
          sectionLabel: this.getStatusLabel ? this.getStatusLabel(rawStatus) : rawStatus,
          badgeClass: 'badge-' + badgeColor,
        };
      });
    },

    barChartSubtitle() {
      const subtitles = {
        week: this.dashboardText("จำนวน Ticket แยกตามวัน", "Tickets by Day"),
        month: this.dashboardText("จำนวน Ticket แยกตามเดือน", "Tickets by Month"),
        year: this.dashboardText("จำนวน Ticket แยกตามปี (5 ปี)", "Tickets by Year (5 Years)"),
        custom: this.$t("dashboard.rangeCustom"),
        all: this.$t("dashboard.subtitleAll"),
      };
      return subtitles[this.selectedRange] || "";
    },

    barLabels() {
      const now = moment();
      switch (this.selectedRange) {
        case "week":
          return this.dashboardText(
            ["จ.", "อ.", "พ.", "พฤ.", "ศ.", "ส.", "อา."],
            ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
          );
        case "month":
          return this.dashboardText(
            ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."],
            ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
          );
        case "year":
        case "all":
        default: {
          const currentYear = now.year();
          const adYears = Array.from({ length: 5 }, (_, i) => String(currentYear - 4 + i));
          const beYears = Array.from({ length: 5 }, (_, i) => String(currentYear - 4 + i + 543));
          return this.dashboardText(beYears, adYears);
        }
        case "custom": {
          if (!this.startDate || !this.endDate) return [];
          const start = moment(this.startDate);
          const days = moment(this.endDate).diff(start, "days") + 1;
          return Array.from({ length: Math.max(0, days) }, (_, index) => start.clone().add(index, "days").format("DD/MM"));
        }
      }
    },

    allYears() {
      if (!this.tickets.length) return [`${moment().year()}`];
      const years = this.tickets.map((ticket) => this.getTicketDate(ticket).year());
      const minYear = Math.min(...years);
      const maxYear = Math.max(...years);
      return Array.from({ length: maxYear - minYear + 1 }, (_, index) => `${minYear + index}`);
    },

    barChartValues() {
      const data = new Array(this.barLabels.length).fill(0);
      this.dateFilteredTickets.forEach((ticket) => {
        const date = this.getTicketDate(ticket);
        let index = -1;

        switch (this.selectedRange) {
          case "week": index = date.isoWeekday() - 1; break;
          case "month": index = date.month(); break;
          case "year":
          case "all": {
            const startYear = moment().year() - 4;
            index = date.year() - startYear;
            break;
          }
          case "custom": index = date.clone().startOf("day").diff(moment(this.startDate).startOf("day"), "days"); break;
        }

        if (index >= 0 && index < data.length) data[index]++;
      });
      return data;
    },

    barDatasets() {
      return [
        {
          label: this.$t("ticketDetail.tech.receivedJobsCount"),
          backgroundColor: "#7A1B1C",
          borderColor: "#7A1B1C",
          borderWidth: 0,
          data: this.barChartValues,

          barThickness: 18,
          maxBarThickness: 18,
        },
      ];
    },

    barHasData() {
      return this.barChartValues.some((value) => value > 0);
    },

    barTrendItems() {
      const labels = this.barLabels || [];
      const values = this.barChartValues || [];
      const maxVal = Math.max(1, ...values);
      return labels.map((label, idx) => {
        const count = values[idx] || 0;
        return {
          label,
          count,
          height: Math.max(3, Math.round((count / maxVal) * 150)),
        };
      });
    },

    barOptions() {
      return {
        maintainAspectRatio: false,
        responsive: true,
        legend: { display: true, labels: { boxWidth: 28, fontColor: "#666666", fontSize: 11 } },
        tooltips: {
          displayColors: false,
          backgroundColor: "#1e3a8a",
          titleFontColor: "#ffffff",
          bodyFontColor: "#ffffff",
          cornerRadius: 6,
          xPadding: 10,
          yPadding: 9,
        },
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
              suggestedMax: 5,
              stepSize: 1,
              precision: 0,
              fontColor: "#777777",
              fontSize: 10,
              padding: 8,
            },
            gridLines: {
              color: "rgba(122,27,28,0.10)",
              zeroLineColor: "rgba(122,27,28,0.16)",
              borderDash: [3, 3],
              drawBorder: false,
            },
          }],
          xAxes: [{
            ticks: { fontColor: "#666666", fontSize: 10, padding: 8 },
            gridLines: { display: false, drawBorder: false },
          }],
        },
      };
    },

    reportFields() {
      return [
        { key: "ticketNo", label: this.$t("dashboard.field.ticketNo"), _classes: "font-weight-bold", _style: "min-width:130px;" },
        { key: "dateInformed", label: this.$t("dashboard.field.dateInformed"), _style: "min-width:120px;" },
        { key: "reporter", label: this.$t("dashboard.field.reporterName"), _style: "min-width:160px;" },
        { key: "category", label: this.$t("dashboard.field.category"), _style: "min-width:180px;" },
        { key: "sla", label: this.$t("dashboard.field.sla"), _style: "min-width:100px;" },
        { key: "status", label: this.$t("dashboard.field.status"), _style: "min-width:120px;" },
        { key: "assignee", label: this.$t("dashboard.field.assignee"), _style: "min-width:150px;" },
        { key: "closedDate", label: this.$t("dashboard.field.closedDate"), _style: "min-width:120px;" },
      ];
    },

    formattedTickets() {
      return this.dateFilteredTickets.map((ticket) => {
        const subCategory = ticket.userPart?.subCategory?.name || ticket.userPart?.subCategory || "-";
        const slaHours = this.getSlaHours(subCategory);
        const startDate = this.getTicketDate(ticket);
        const deadline = startDate.clone().add(slaHours, "hours");
        const isClosed = ["done", "welldone"].includes(ticket.status);
        const endDate = isClosed ? moment(ticket.updatedAt) : moment();
        const isOverdue = endDate.isAfter(deadline);
        const effStatus = getTechnicianEffectiveStatus(ticket, this.currentTechnicianId);

        return {
          ticketNo: ticket.ticketNumber || String(ticket._id || "N/A").substring(0, 8).toUpperCase(),
          dateInformed: this.formatDate(ticket.userPart?.dateTimeInformed || ticket.createdAt),
          reporterDepartment: this.getReporterDepartment(ticket),
          reporter: this.getReporterName(ticket),
          category: subCategory,
          status: this.getStatusLabel(effStatus),
          sla: isOverdue ? this.$t("dashboard.slaOverdue") : this.$t("dashboard.slaOnTime"),
          assignee: this.getAssignee(ticket),
          closedDate: isClosed ? this.formatDate(ticket.updatedAt) : "-",
          _status_raw: effStatus,
          _sla_hours: slaHours,
          _slaOverdue: isOverdue,
        };
      });
    },
  },

  watch: {
    searchQuery(val) {
      this.hasNavigatedMatch = false;
      if (val && val.trim()) {
        this.showSearchDropdown = true;
        this.currentMatchIndex = 0;
      } else {
        this.currentMatchIndex = 0;
        this.showSearchDropdown = false;
      }
    },
    currentTechnicianId(val) {
      if (val) this.fetchData();
    },
    selectedRange() {
      this.calculateStats();
    },
    "$route.path"(newPath) {
      if (newPath === "/technician/maintechnician") {
        this.fetchCurrentRepairWorkload();
      }
    },
  },

  mounted() {
    this.fetchData();
    this.fetchCurrentRepairWorkload();
    document.addEventListener('click', this.handleDocumentClick);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleDocumentClick);
  },

  methods: {
    dashboardText(thaiText, englishText) {
      const locale = String(
        this.$i18n?.locale || this.$store?.state?.locale?.lang || "th"
      ).toLowerCase();
      return locale.startsWith("th") ? thaiText : englishText;
    },

    removeMatchHighlight() {},
    goToTicketDetail(item) {
      if (!item) return;
      const ticketId = item._id || item.ticketNo || item.id;
      if (!ticketId || ticketId === '-') return;
      const rawStatus = (item._status_raw || item.status || '').toLowerCase();
      const isFinished = ['done', 'welldone', 'cancel', 'closed'].includes(rawStatus);
      if (isFinished) {
        this.$router.push({ path: '/technician/history', query: { ticketId } });
      } else {
        this.$router.push({ path: '/technician/maintechnician', query: { ticketId } });
      }
    },

    handleDocumentClick(e) {
      if (this.$refs.searchBoxRef && !this.$refs.searchBoxRef.contains(e.target)) {
        this.showSearchDropdown = false;
      }
    },

    clearSearch() {
      this.searchQuery = '';
      this.showSearchDropdown = false;
    },

    closeSearchDropdown() {
      this.showSearchDropdown = false;
    },

    onSelectSearchResult(m) {
      if (!m || !m.ticket) return;
      this.showSearchDropdown = false;
      const ticket = m.ticket;
      const ticketId = ticket._id || ticket.id || '';
      const ticketNo = ticket.ticketNumber || (ticketId ? ticketId.substring(0, 8).toUpperCase() : '');
      const isFinished = ['done', 'welldone', 'cancelled'].includes(ticket.status);

      if (isFinished) {
        this.$router.push({
          path: '/technician/history',
          query: { ticketId, ticketNo, highlight: 'true' }
        });
      } else {
        const filter = ticket.status === 'pending' ? 'unassigned' : 'active';
        this.$router.push({
          path: '/technician/maintechnician',
          query: { filter, ticketId, ticketNo, highlight: 'true' }
        });
      }
    },

    scrollElementToCenter(target) {
      if (!target) return;
      const scrollContainers = [
        document.querySelector('.c-body'),
        document.querySelector('.c-main'),
        document.querySelector('.role-theme-app'),
        document.scrollingElement,
        document.documentElement,
        document.body
      ];
      const container = scrollContainers.find(
        c => c && c.scrollHeight > c.clientHeight && (typeof window === 'undefined' || window.getComputedStyle(c).overflowY !== 'hidden')
      );
      const bottomNavHeight = 90;
      const headerHeight = 70;

      if (container && container !== document.documentElement && container !== document.body) {
        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const currentScroll = container.scrollTop;
        const relativeTargetTop = targetRect.top - containerRect.top + currentScroll;
        const effectiveHeight = Math.max(100, containerRect.height - bottomNavHeight);
        const idealTop = relativeTargetTop - (effectiveHeight / 2) + (targetRect.height / 2);

        container.scrollTo({
          top: Math.max(0, idealTop),
          behavior: 'smooth'
        });
      } else {
        const targetRect = target.getBoundingClientRect();
        const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        const effectiveHeight = Math.max(100, windowHeight - headerHeight - bottomNavHeight);
        const idealTop = (currentScrollY + targetRect.top) - headerHeight - (effectiveHeight / 2) + (targetRect.height / 2);

        window.scrollTo({
          top: Math.max(0, idealTop),
          behavior: 'smooth'
        });
      }
    },

    scrollToAndHighlight(ids) {
      const isVisible = (el) => {
        if (!el) return false;
        const rect = el.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) return false;
        if (el.offsetParent === null && (typeof window === 'undefined' || window.getComputedStyle(el).position !== 'fixed')) {
          return false;
        }
        return true;
      };

      let foundTarget = null;
      for (const id of ids) {
        if (!id) continue;
        const rawId = String(id).replace(/^ticket-row-|^m-tech-rep-/, '');
        const candidates = [
          document.getElementById('m-tech-rep-' + rawId),
          document.getElementById(id),
          document.getElementById('ticket-row-' + rawId),
          ...(this.$el ? Array.from(this.$el.querySelectorAll(`[data-ticket-id="${rawId}"], [data-ticket-no="${rawId}"]`)) : [])
        ].filter(Boolean);

        for (const el of candidates) {
          const target = el.closest('.mobile-ticket-card') ||
                         el.closest('tr') ||
                         el;
          if (isVisible(target)) {
            target.classList.remove('match-highlight-active');
            void target.offsetWidth;
            target.classList.add('match-highlight-active');
            setTimeout(() => {
              target.classList.remove('match-highlight-active');
            }, 3600);
            if (!foundTarget) {
              foundTarget = target;
            }
          }
        }
      }

      if (foundTarget) {
        this.scrollElementToCenter(foundTarget);
        return true;
      }
      return false;
    },

    onSearchEnter() {
      if (this.searchMatches && this.searchMatches.length > 0) {
        this.onSelectSearchResult(this.searchMatches[0]);
      }
    },

    async fetchCurrentRepairWorkload() {
      try {
        this.workloadLoading = true;
        this.workloadError = null;
        const response = await Service.ticket("get", null, {
          urlAppend: "/workload/current"
        });
        if (response.data.success && response.data.data) {
          this.currentRepairWorkload = {
            personId: response.data.data.personId || null,
            name: response.data.data.name || "",
            surname: response.data.data.surname || "",
            role: response.data.data.role || "technician",
            currentWorkload: Number(response.data.data.currentWorkload) || 0,
            workloadPercent: Number(response.data.data.workloadPercent) || 0,
            activeTicketCount: Number(response.data.data.activeTicketCount) || 0,
            currentTickets: response.data.data.currentTickets || [],
          };
        } else {
          this.currentRepairWorkload = {
            personId: null,
            name: "",
            surname: "",
            role: "technician",
            currentWorkload: 0,
            workloadPercent: 0,
            activeTicketCount: 0,
            currentTickets: [],
          };
        }
      } catch (error) {
        console.error("Error fetching current repair workload:", error);
        this.workloadError = error.message;
        this.currentRepairWorkload = {
          personId: null,
          name: "",
          surname: "",
          role: "technician",
          currentWorkload: 0,
          workloadPercent: 0,
          activeTicketCount: 0,
          currentTickets: [],
        };
      } finally {
        this.workloadLoading = false;
      }
    },
    selectRange(range) {
      const now = moment();
      this.selectedRange = range;
      if (range === "all") {
        this.startDate = "";
        this.endDate = "";
        return;
      }
      if (range === "custom") return;
      const boundaries = {
        week: [now.clone().startOf("isoWeek"), now.clone().endOf("isoWeek")],
        month: [now.clone().startOf("year"), now.clone().endOf("year")],
        year: [now.clone().subtract(4, "years").startOf("year"), now.clone().endOf("year")],
      };
      const dates = boundaries[range];
      if (dates) {
        this.startDate = dates[0].format("YYYY-MM-DD");
        this.endDate = dates[1].format("YYYY-MM-DD");
      }
    },

    onCustomDateChange() {
      if (this.startDate && this.endDate && moment(this.endDate).isBefore(moment(this.startDate), "day")) {
        this.endDate = this.startDate;
      }
      this.calculateStats();
    },

    async fetchData() {
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        const technicianId = user?._id || "";
        if (!technicianId) {
          console.warn("No technician ID found");
          return;
        }

        const [ticketsResponse, categoriesResponse] = await Promise.all([
          Service.ticket("get", null, { urlAppend: `/technician/${technicianId}/assigned` }),
          Service.category("get"),
        ]);

        if (ticketsResponse.data.success && Array.isArray(ticketsResponse.data.data)) {
          this.tickets = ticketsResponse.data.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        }

        if (categoriesResponse.data.success) {
          this.categories = categoriesResponse.data.data || [];
        }

        this.calculateStats();
      } catch (error) {
        console.error("Error fetching technician dashboard:", error);
      }
    },

    calculateStats() {
      const tickets = this.dateFilteredTickets;
      const inProgress = tickets.filter((ticket) => ["pending", "assigned", "in_progress"].includes(ticket.status)).length;
      const completed = tickets.filter((ticket) => ["done", "welldone"].includes(ticket.status)).length;
      const overdue = tickets.filter((ticket) => this.isTicketOverdue(ticket)).length;
      const ratedTickets = tickets.filter((ticket) => Number(ticket.rating?.stars) > 0);
      const rating = ratedTickets.length
        ? (ratedTickets.reduce((total, ticket) => total + Number(ticket.rating.stars), 0) / ratedTickets.length).toFixed(1)
        : 0;

      this.stats = { total: tickets.length, inProgress, completed, overdue, satisfaction: rating };
    },

    getTicketDate(ticket) {
      return moment(ticket.userPart?.dateTimeInformed || ticket.createdAt);
    },

    getSlaHours(subCategory) {
      for (const category of this.categories) {
        const sub = category.subCategories?.find((item) => item.name === subCategory);
        if (sub?.slaHours) return Number(sub.slaHours);
      }
      return 24;
    },

    isTicketOverdue(ticket) {
      const subCategory = ticket.userPart?.subCategory?.name || ticket.userPart?.subCategory;
      const slaHours = this.getSlaHours(subCategory);
      const deadline = this.getTicketDate(ticket).clone().add(slaHours, "hours");
      const end = ["done", "welldone"].includes(ticket.status) ? moment(ticket.updatedAt) : moment();
      return end.isAfter(deadline);
    },

    getReporterName(ticket) {
      const user = ticket.userPart?.user;
      if (user && typeof user === "object") {
        const name = `${user.name || ""} ${user.surname || ""}`.trim();
        return name || "-";
      }
      return ticket.userPart?.fullname || ticket.userPart?.username || "-";
    },

    getReporterDepartment(ticket) {
      return getTicketReporterAffiliation(ticket);
    },

    getReporterAffiliation(ticket) {
      return getTicketReporterAffiliation(ticket);
    },

    getAssignee(ticket) {
      const tech = ticket.supervisorPart?.assignedTechnician;
      if (tech && typeof tech === "object") return `${tech.name || ""} ${tech.surname || ""}`.trim() || "-";
      return "-";
    },

    formatDate(date) {
      if (!date) return "-";
      const isEn = this.currentLang === "en";
      const m = moment(date);
      if (!m.isValid()) return "-";

      const day = m.format("DD");
      const time = m.format("HH:mm");

      if (isEn) {
        const enMonths = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = enMonths[m.month()];
        const year = m.format("YYYY");
        return `${day} ${month} ${year} ${time}`;
      } else {
        const thaiMonths = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
        const month = thaiMonths[m.month()];
        const year = String(m.year() + 543).slice(-2);
        return `${day} ${month} ${year} ${time} น.`;
      }
    },

    getStatusLabel(status) {
      const key = `dashboard.status.${status}`;
      const text = this.$t(key);
      return text !== key ? text : status;
    },

    getBadge(status) {
      const colors = {
        pending: "warning",
        assigned: "info",
        in_progress: "primary",
        done: "success",
        welldone: "success",
        cancelled: "secondary",
        rejected: "danger",
      };
      return colors[status] || "secondary";
    },

    scrollToTable() {
      this.$nextTick(() => {
        const el = this.$refs.ticketPanel;
        if (el) {
          const yOffset = -20;
          const scrollContainers = [
            document.querySelector(".c-body"),
            document.querySelector(".c-main"),
            document.documentElement,
          ];
          const container = scrollContainers.find((c) => c && c.scrollHeight > c.clientHeight);
          if (container && container !== document.documentElement) {
            const top = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop + yOffset;
            container.scrollTo({ top, behavior: "smooth" });
          } else {
            const top = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top, behavior: "smooth" });
          }
        }
      });
    },
  },
};
</script>

<style scoped>

/* =====================================================
   ROOT
===================================================== */

.technician-dashboard {
  --admin-primary: #1d4ed8;
  --admin-dark: #1e3a8a;
  --admin-indigo: #1e40af;
  --success: #059669;
  --warning: #d97706;
  --danger: #dc2626;
  --rating: #f59e0b;
  --page-bg: #eff6ff;
  --surface: #ffffff;
  --border: #bfdbfe;
  --border-soft: #dbeafe;
  --text: #0f172a;
  --muted: #64748b;

  width: 100%;
  min-width: 0;
  min-height: 100vh;

  margin: 0;
  margin-top: 0;
  padding: 0 0 28px;

  overflow: visible;

  background: #eff6ff !important;
  color: var(--text);

  font-family: "Sarabun", sans-serif;
  font-size: 14px;
  font-weight: 400;

  box-sizing: border-box;
}

/* =====================================================
   DASHBOARD HEADER
===================================================== */

.dashboard-sticky-area {
  position: sticky !important;
  top: var(--app-header-height) !important;
  z-index: 100 !important;
  margin: 0 0 16px 0 !important;
  padding: 0 !important;
  width: 100%;
  background: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  overflow: visible;
}

.dashboard-topbar,
.dashboard-heading {
  width: 100%;
  min-width: 0;
  height: 112px;
  min-height: 112px;
  margin: 0;
  padding: 22px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 50%, #1e40af 100%) !important;
  border: 0;
  border-radius: 16px !important;
  box-shadow: 0 4px 18px rgba(30, 58, 138, 0.25) !important;
  box-sizing: border-box;
}

.dashboard-page-title,
.dashboard-title {
  margin: 0 !important;
  padding: 0 !important;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #ffffff !important;
  font-size: clamp(1.4rem, 2vw, 1.9rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.2;
  min-width: 0;
  flex: 1 1 auto;
  white-space: nowrap;
}

.dashboard-title-icon,
.dashboard-page-title svg,
.dashboard-title svg,
.dashboard-page-title .c-icon,
.dashboard-title .c-icon {
  color: #ffffff !important;
  fill: currentColor !important;
  font-size: 1.6rem;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
}

/* =====================================================
   RANGE SELECTOR & FILTERS
===================================================== */

.dashboard-filter {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
}

.range-tabs {
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
}

.range-tab {
  padding: 8px 16px;
  font-size: 0.92rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: transparent;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.range-tab:hover {
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
}

.range-tab.active {
  background: #ffffff;
  color: #1d4ed8;
  font-weight: 800;
  border-color: #ffffff;
}

.custom-date-range {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  padding: 4px 10px;
  border-radius: 8px;
  border: none;
}

.custom-date-range input {
  border: 0;
  font-size: 0.88rem;
  font-weight: 600;
  color: #0f172a;
}

.custom-date-range span {
  color: #777777;
  font-size: 0.85rem;
}

.custom-date-range .range-tab {
  min-height: 28px;
  padding: 4px 8px;
  border: 0;
  border-left: 1px solid #dddddd;
  color: #1d4ed8;
  border-radius: 0;
}

.filter-tab {
  padding: 8px 12px;
  font-size: 16px;
}

.filter-tab,
.filter-tab svg,
::v-deep .filter-tab .c-icon {
  color: #ffffff !important;
}

.filter-tab.active,
.filter-tab.active svg,
::v-deep .filter-tab.active .c-icon {
  color: #1d4ed8 !important;
}

/* =====================================================
   KPI
===================================================== */

.kpi-grid {
  width: 100%;
  min-width: 0;

  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));

  gap: 18px;

  margin: 0 0 18px;
  padding: 0;

  box-sizing: border-box;
}

.kpi-card,
.kpi-primary {
  width: 100%;
  min-width: 0;
  min-height: 132px;
  height: auto;

  padding: 18px 20px;

  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: space-between;

  background: #ffffff;

  border: 1.5px solid var(--border-soft);
  border-radius: 14px;

  box-shadow: 0 2px 8px rgba(30, 58, 138, 0.04);
}

.kpi-clickable {
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}

.kpi-clickable:hover {
  transform: translateY(-2px);
  border-color: #93c5fd;
  box-shadow: 0 6px 18px rgba(30, 58, 138, 0.1);
}

.kpi-clickable:active {
  transform: translateY(-1px);
}

.kpi-card-head {
  width: 100%;

  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;

  gap: 8px;

  color: #475569;

  font-size: 0.92rem;
  font-weight: 700;
  letter-spacing: 0.01em;

  text-align: left;
}

.kpi-icon {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin: 0;

  border-radius: 10px;
  font-size: 18px;
}

.icon-blue {
  color: #1e40af;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
}

.icon-orange {
  color: #7c3aed;
  background: #faf5ff;
  border: 1px solid #e9d5ff;
}

.icon-red {
  color: #dc2626;
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.icon-green {
  color: #059669;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.soft-orange {
  color: #7c3aed;
  background: #faf5ff;
}

.soft-green {
  color: #059669;
  background: #f0fdf4;
}

.soft-red {
  color: #dc2626;
  background: #fef2f2;
}

.soft-yellow {
  color: #d97706;
  background: #fffbeb;
}

.kpi-value {
  color: #0f172a;

  font-size: 2.2rem;
  font-weight: 800;
  line-height: 1.1;
  margin: 6px 0;
}

.kpi-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: var(--muted);
  font-size: 0.8rem;
}

.kpi-badge {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  display: inline-flex;
  align-items: center;
}

.kpi-badge-blue {
  background: #eff6ff;
  color: #1e40af;
  border: 1px solid #bfdbfe;
}

.kpi-badge-active {
  background: #faf5ff;
  color: #7c3aed;
  border: 1px solid #e9d5ff;
}

.kpi-badge-red {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.kpi-badge-green {
  background: #f0fdf4;
  color: #059669;
  border: 1px solid #bbf7d0;
}

.kpi-rating-badge {
  background: #fffbeb;
  color: #b45309;
  border: 1px solid #fde68a;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 0.76rem;
  font-weight: 700;
}

/* =====================================================
   MAIN
===================================================== */

.dashboard-main-grid {
  width: 100%;

  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(330px, 0.82fr);

  gap: 18px;

  margin-bottom: 18px;
}

.dashboard-left,
.dashboard-right {
  min-width: 0;
}

.panel-card {
  width: 100%;
  background: #ffffff;
  border: 1.5px solid #dbeafe;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(30, 58, 138, 0.04);
}

/* =====================================================
   PANEL HEADER
===================================================== */

.panel-header {
  width: 100%;
  min-height: 54px;

  padding: 12px 18px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 12px;

  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
}

.panel-header h4 {
  margin: 0;

  color: #1e40af !important;

  font-size: 1.15rem;
  font-weight: 800;
}

.panel-header p {
  margin: 2px 0 0;

  color: var(--muted);

  font-size: 0.85rem;
}

.panel-more {
  width: 29px;
  height: 29px;

  padding: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border: 0;

  background: transparent;
  color: var(--admin-primary);
}

.panel-more,
.panel-more svg,
::v-deep .panel-more .c-icon {
  color: var(--admin-primary) !important;
}

.chart-divider {
  display: none;
}

/* =====================================================
   CHART
===================================================== */

.highlight-card {
  height: 100%;
}

.chart-wrapper {
  position: relative;
  width: 100%;
  min-height: 280px;
  height: auto;
  padding: 12px 16px;
  overflow: visible;
}

.chart-wrapper > div {
  width: 100% !important;
}

::v-deep .chart-wrapper canvas {
  width: 100% !important;
  height: 100% !important;
}

.empty-chart {
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  gap: 8px;

  color: #aaaaaa;
}

/* =====================================================
   PROGRESS & DONUT CHART (IMPECCABLE REDESIGN)
===================================================== */

.progress-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.progress-body {
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  flex: 1;
}

.donut-wrapper {
  position: relative;
  width: 190px;
  height: 190px;
  margin: 6px auto 18px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.donut-svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
  overflow: visible;
}

.donut-background {
  fill: none;
  stroke: #eff6ff;
  stroke-width: 14;
}

.donut-progress {
  fill: none;
  stroke: #1d4ed8;
  stroke-width: 14;
  stroke-linecap: round;
  transition: stroke-dasharray 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  filter: drop-shadow(0 2px 6px rgba(29, 78, 216, 0.25));
}

.donut-center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.donut-percentage {
  color: #0f172a;
  font-size: 2.1rem;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.02em;
}

.donut-caption {
  margin-top: 5px;
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.progress-stats-grid {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.progress-stat-pill {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 14px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  transition: all 0.15s ease;
}

.progress-stat-pill:hover {
  background: #ffffff;
  border-color: #cbd5e1;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
}

.stat-pill-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
}

.stat-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dot-green { background: #059669; box-shadow: 0 0 0 2px rgba(5, 150, 105, 0.15); }
.dot-purple { background: #7c3aed; box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.15); }
.dot-blue { background: #1d4ed8; box-shadow: 0 0 0 2px rgba(29, 78, 216, 0.15); }

.stat-pill-val {
  font-size: 1.05rem;
  font-weight: 800;
}

/* =====================================================
   TICKET
===================================================== */

.ticket-panel {
  margin-top: 14px;
}

.ticket-header {
  align-items: center;
}

.print-btn {
  border: 1px solid rgba(255, 255, 255, 0.85) !important;

  background: transparent !important;
  color: #ffffff !important;

  box-shadow: none !important;
}

.print-btn:hover {
  background: #ffffff !important;
  color: var(--admin-primary) !important;
}

.ticket-table-wrapper {
  width: 100%;

  padding: 18px;

  overflow-x: auto;

  background: #ffffff;
}

/* =====================================================
   DATATABLE DESKTOP
===================================================== */

::v-deep .table {
  width: 100%;
  min-width: 850px;

  margin-bottom: 0;

  font-family: "Sarabun", sans-serif;
}

::v-deep .table thead th {
  padding: 10px;

  background: #f8f9fa;
  color: #495057;

  font-size: 0.75rem;
  font-weight: 700;
  border-bottom: 2px solid #dee2e6;

  white-space: nowrap;
}

::v-deep .table tbody td {
  padding: 10px;

  border-top: 0;
  border-bottom: 1px solid var(--border-soft);

  color: #444444;

  font-size: 0.8rem;

  white-space: nowrap;
}

::v-deep .table tbody tr:hover td {
  background: #fbf8f8;
}

/* =====================================================
   DATATABLE CONTROLS
===================================================== */

::v-deep .form-inline,
::v-deep .form-inline label,
::v-deep .c-datatable-filter,
::v-deep .c-datatable-filter label {
  font-family: "Sarabun", sans-serif;
  font-size: 0.8rem;
}

::v-deep .form-inline {
  margin-bottom: 8px;
}

::v-deep .form-inline label {
  margin-right: 6px;
}

::v-deep .form-control,
::v-deep .custom-select {
  min-height: 32px;

  border: 1px solid #dee2e6;
  border-radius: 4px;

  font-size: 0.75rem;

  box-shadow: none;
}

::v-deep .form-control:focus,
::v-deep .custom-select:focus {
  border-color: var(--admin-primary);

  box-shadow: 0 0 0 2px rgba(122, 27, 28, 0.08);
}



/* =====================================================
   TABLET
===================================================== */

@media (max-width: 1200px) {
  .dashboard-main-grid {
    grid-template-columns: 1fr;
  }

  .dashboard-right {
    margin-top: 0;
  }
}

@media (max-width: 768px) {
  .dashboard-sticky-area {
    top: 0 !important;
    margin: 0 0 12px 0 !important;
    padding: 0 !important;
    background: transparent !important;
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
  }

  .technician-dashboard {
    margin-top: 0;
    padding: 8px 0 16px;
  }

  .dashboard-topbar,
  .dashboard-heading {
    flex-direction: column;
    align-items: stretch;
    min-height: auto;
    height: auto;
    margin-bottom: 0;
    padding: 14px 16px;
    gap: 12px;
  }

  .dashboard-page-title,
  .dashboard-title {
    font-size: 1.15rem;
    gap: 8px;
  }

  .dashboard-title-icon,
  .dashboard-page-title svg,
  .dashboard-title svg,
  .dashboard-page-title .c-icon,
  .dashboard-title .c-icon {
    width: 20px;
    height: 20px;
    font-size: 1.2rem;
  }

  .range-selector {
    width: fit-content;
    max-width: 100%;
    align-self: flex-end;
    justify-content: flex-start;
  }

  .range-selector > .range-button:not(.filter-icon-button) {
    width: auto;
    flex: 0 0 auto;
  }

  .range-selector > .filter-icon-button {
    width: 38px;
    flex: 0 0 38px;
  }

  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    gap: 10px;

    margin-top: 0;
    padding: 0;
  }

  .kpi-card,
  .kpi-primary {
    height: 125px;

    padding: 12px;
  }

  .chart-wrapper {
    height: 300px;

    padding: 14px 8px 10px;
  }
}

/* =====================================================
   MOBILE
===================================================== */

@media (max-width: 576px) {
  .technician-dashboard {
    margin-top: 0;
    padding: 8px 0 14px;
  }

  .dashboard-sticky-area {
    top: 0 !important;
    margin: 0 0 10px 0 !important;
    padding: 8px 0 6px 0 !important;
    background: rgba(239, 246, 255, 0.92) !important;
    backdrop-filter: blur(16px) saturate(180%) !important;
    -webkit-backdrop-filter: blur(16px) saturate(180%) !important;
  }

  /* HEADER */

  .dashboard-topbar,
  .dashboard-heading {
    min-height: auto;
    height: auto;
    margin-bottom: 0;
    padding: 12px 14px;
    gap: 9px;
  }

  .dashboard-page-title,
  .dashboard-title {
    font-size: 1.05rem;
    gap: 6px;
  }

  .dashboard-title-icon,
  .dashboard-page-title svg,
  .dashboard-title svg,
  ::v-deep .dashboard-page-title .c-icon,
  ::v-deep .dashboard-title .c-icon {
    width: 18px;
    height: 18px;
  }

  .custom-date-filter {
    margin-top: 0;
    padding: 10px 12px;
    align-items: stretch;
    flex-direction: column;
  }

  .range-button {
    min-height: 32px;

    padding: 0 9px;

    font-size: 0.7rem;
  }

  /* KPI */

  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    margin-top: 0;
    padding: 0;

    gap: 8px;
  }

  .kpi-card,
  .kpi-primary {
    height: 120px;

    padding: 12px;
  }

  .kpi-card:nth-child(5) {
    grid-column: auto;
    width: 100%;
    justify-self: stretch;
  }

  .kpi-card-head {
    font-size: 9px;
  }

  .kpi-icon {
    width: 34px;
    height: 34px;
    flex-basis: 34px;
  }

  .kpi-value {
    font-size: 24px;
  }

  /* CHART */

  .chart-wrapper {
    height: 270px;

    padding: 12px 4px 8px;
  }

  /* TICKET HEADER */

  .ticket-header {
    flex-wrap: nowrap !important;

    gap: 8px;
  }

  .ticket-header h4 {
    flex: 1 1 auto;

    min-width: 0;

    overflow: hidden;

    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .print-btn {
    flex: 0 0 auto;

    white-space: nowrap;
  }

  /* =====================================================
     MOBILE TICKET
     ใช้สีและหน้าตาเดิม
  ===================================================== */

  .ticket-table-wrapper {
    padding: 10px;

    overflow-x: hidden;
  }

  ::v-deep .c-datatable > .row:first-child {
    display: flex !important;

    flex-direction: column !important;
    align-items: flex-start !important;

    gap: 8px !important;
  }

  ::v-deep .c-datatable > .row:first-child > div {
    width: 100% !important;
    max-width: 100% !important;

    flex: 0 0 100% !important;
  }

  ::v-deep .form-inline,
  ::v-deep .c-datatable-filter {
    display: flex !important;

    width: 100% !important;

    align-items: center !important;

    flex-wrap: nowrap !important;

    margin: 0 0 8px !important;
  }

  ::v-deep .form-inline label,
  ::v-deep .c-datatable-filter label {
    margin-right: 6px !important;
    margin-bottom: 0 !important;

    white-space: nowrap;
  }

  ::v-deep .form-inline .form-control,
  ::v-deep .c-datatable-filter .form-control {
    width: auto !important;
    max-width: 150px !important;
  }

  ::v-deep .form-inline select.form-control,
  ::v-deep .form-inline .custom-select {
    width: 70px !important;
  }

  /*
    ยกเลิก min-width 850px เฉพาะมือถือ
  */

  ::v-deep .table {
    width: 100% !important;
    min-width: 0 !important;

    display: block !important;

    margin-top: 8px;
  }

  ::v-deep .table thead {
    display: none;
  }

  ::v-deep .table tbody {
    width: 100%;

    display: block;
  }

  /*
    1 ticket = card
    แต่ยังใช้สีขาว / เส้น / เงาแบบเดิม
  */

  ::v-deep .table tbody tr {
    width: 100%;

    margin-bottom: 10px;
    padding: 10px 12px;

    display: block;

    background: #ffffff;

    border: 1px solid var(--border-soft);
    border-radius: 6px;

    box-shadow: 0 2px 7px rgba(0, 0, 0, 0.035);
  }

  ::v-deep .table tbody td {
    width: 100% !important;
    min-width: 0 !important;

    padding: 7px 0 !important;

    display: flex !important;
    align-items: center;
    justify-content: space-between;

    gap: 12px;

    border: 0 !important;
    border-bottom: 1px solid var(--border-soft) !important;

    background: #ffffff !important;

    font-size: 0.75rem;

    white-space: normal !important;
  }

  ::v-deep .table tbody td:last-child {
    border-bottom: 0 !important;
  }

  ::v-deep .table tbody td::before {
    content: attr(data-label);
    flex: 0 0 40%;
    color: var(--muted);
    font-size: 0.7rem;
    font-weight: 700;
  }


}

/* =====================================================
   VERY SMALL MOBILE
===================================================== */

@media (max-width: 360px) {
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .kpi-card:nth-child(5) {
    width: 100%;
    justify-self: stretch;
  }

  .range-button {
    padding: 0 8px;

    font-size: 0.68rem;
  }
}

/* =====================================================
   PRINT
===================================================== */

@media print {
  .c-sidebar,
  .c-header,
  .c-footer,
  .dashboard-topbar,
  .dashboard-heading,
  .kpi-grid,
  .dashboard-main-grid,
  .print-btn {
    display: none !important;
  }

  .technician-dashboard {
    padding: 0;

    background: #ffffff;
  }

  .ticket-panel {
    border: 0;

    box-shadow: none;
  }

  .ticket-table-wrapper {
    overflow: visible;
  }

  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
</style>
